import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../css/admin/admin-mc-connections.css';
import './BillingPlan.css';
import logo from '../../images/audithippo.png';

const PLANS_API_URL       = '/rest/api/v1/admin/billing/plans';
const ADD_PLAN_API_URL    = '/rest/api/v1/admin/billing/plans/add';
const REMOVE_PLAN_API_URL = '/rest/api/v1/admin/billing/plans/delete';
const ADD_PAYMENT_API_URL = '/rest/api/v1/admin/billing/payments';
const PAYMENTS_API_URL    = '/rest/api/v1/admin/billing/payments';
const CONNECTIONS_API_URL = '/rest/api/v1/admin/connections';
const USERS_API_URL       = '/rest/api/v1/admin/users';

const EMPTY_PLAN = {
    name: '',
    annualPrice: '',
    maxUsers: '',
    paymentType: 'card',
    durationMonths: '',
};

const EMPTY_PAYMENT = {
    connectionId: '',
    userId: '',
    planId: '',
    amount: '',
    paymentType: 'card',
    subscriptionEnd: '',
};

const PAYMENT_TYPES = ['card', 'bank', 'invoice'];

function normalizePlan(p) {
    const isCurrent = !!p.isCurrent || p.btnLabel === 'Current Plan';
    let btnClass = p.btnClass || 'btn-admin-primary';
    if (btnClass === 'btn-secondary') btnClass = 'btn-admin-secondary';
    if (isCurrent) btnClass = 'btn-admin-secondary';
    return { ...p, isCurrent, btnClass };
}

function sortPlans(plans) {
    return [...plans].sort((a, b) => {
        const ax = a.maxInstances === -1 ? Infinity : a.maxInstances;
        const bx = b.maxInstances === -1 ? Infinity : b.maxInstances;
        return ax - bx;
    });
}

function formatInvoiceAmount(amt) {
    if (amt == null || amt === '') return '—';
    if (typeof amt === 'string' && amt.startsWith('$')) return amt;
    const cents = Number(amt);
    if (!Number.isFinite(cents)) return String(amt);
    return `$${(cents / 100).toFixed(2)}`;
}

function normalizeInvoice(item, idx) {
    const dateStr = item.paymentDate || item.date || item.createdAt || item.paidAt || item.subscriptionEnd || '';
    const d = dateStr ? new Date(dateStr) : null;
    const validDate = d && !isNaN(d);
    return {
        id:             item.id || item.invoiceId || item.paymentId || `INV-${idx + 1}`,
        connectionId:   item.connectionId || item.connection?.id || '',
        connectionName: item.connectionName || item.connection?.name || '',
        userId:         item.adminUserId || item.userId || item.user?.id || '',
        userName:       item.adminName || item.userName || item.user?.name || item.adminEmail || item.user?.email || '',
        period:         item.period || (validDate ? d.toLocaleString('en-US', { month: 'short', year: 'numeric' }) : '—'),
        date:           validDate ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : (item.date || '—'),
        amount:         formatInvoiceAmount(item.amount ?? item.total ?? item.price),
        status:         item.status || 'completed',
    };
}

function BillingPlan() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [isAnnual, setIsAnnual] = useState(false);
    const [plans, setPlans] = useState([]);
    const [connections, setConnections]             = useState([]);
    const [planOpen, setPlanOpen]                   = useState(false);
    const [planForm, setPlanForm]                   = useState(EMPTY_PLAN);
    const [planSubmitting, setPlanSubmitting]       = useState(false);
    const [planError, setPlanError]                 = useState(null);
    const [paymentOpen, setPaymentOpen]             = useState(false);
    const [paymentForm, setPaymentForm]             = useState(EMPTY_PAYMENT);
    const [paymentSubmitting, setPaymentSubmitting] = useState(false);
    const [paymentError, setPaymentError]           = useState(null);
    const [invoices, setInvoices]                   = useState([]);
    const [users, setUsers]                         = useState([]);
    const [removingPlanId, setRemovingPlanId]       = useState(null);
    const [showPlanValidation, setShowPlanValidation]       = useState(false);
    const [showPaymentValidation, setShowPaymentValidation] = useState(false);

    const fetchInvoices = () => axios.get(PAYMENTS_API_URL)
        .then(res => {
            const data = res.data;
            const list = Array.isArray(data) ? data : (data.items || data.payments || data.data || []);
            setInvoices(list.map(normalizeInvoice));
        })
        .catch(() => {});

    const fetchPlans = () => axios.get(PLANS_API_URL)
        .then(res => {
            const data = res.data;
            const list = Array.isArray(data) ? data : (data.items || data.plans || data.data || []);
            setPlans(sortPlans(list.map(normalizePlan)));
        })
        .catch(() => {});

    const fetchConnections = () => axios.get(CONNECTIONS_API_URL)
        .then(res => {
            const data = res.data;
            const list = Array.isArray(data) ? data : (data.items || data.connections || data.data || []);
            setConnections(list);
        })
        .catch(() => {});

    const fetchUsers = () => axios.get(USERS_API_URL)
        .then(res => {
            const data = res.data;
            const list = Array.isArray(data) ? data : (data.items || data.users || data.data || []);
            setUsers(list);
        })
        .catch(() => {});

    useEffect(() => {
        fetchPlans();
        fetchConnections();
        fetchInvoices();
        fetchUsers();
    }, []);

    const handleRemovePlan = (plan) => {
        if (removingPlanId) return;
        const ok = window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`);
        if (!ok) return;
        setRemovingPlanId(plan.id);
        axios.post(REMOVE_PLAN_API_URL, { planId: plan.id })
            .then(() => fetchPlans())
            .catch(err => {
                console.error('[BillingPlan] failed to remove plan:', err.response?.status, err.response?.data, err.message);
                window.alert(`Failed to remove plan: ${err.response?.data?.message || err.message}`);
            })
            .finally(() => setRemovingPlanId(null));
    };

    const openPlan  = () => { setPlanForm(EMPTY_PLAN); setPlanError(null); setShowPlanValidation(false); setPlanOpen(true); };
    const closePlan = () => { if (planSubmitting) return; setPlanOpen(false); setPlanError(null); setShowPlanValidation(false); };
    const updatePlan = (field, value) => setPlanForm(prev => ({ ...prev, [field]: value }));

    const submitPlan = () => {
        if (planSubmitting) return;
        if (!planValid) { setShowPlanValidation(true); return; }
        setShowPlanValidation(false);
        setPlanSubmitting(true);
        setPlanError(null);
        axios.post(ADD_PLAN_API_URL, {
            name:           planForm.name,
            annualPrice:    Number(planForm.annualPrice),
            maxUsers:       Number(planForm.maxUsers),
            paymentType:    planForm.paymentType,
            durationMonths: Number(planForm.durationMonths),
        })
            .then(() => {
                setPlanOpen(false);
                setPlanForm(EMPTY_PLAN);
                fetchPlans();
            })
            .catch(err => setPlanError(err.response?.data?.message || err.message))
            .finally(() => setPlanSubmitting(false));
    };

    const planValid = planForm.name.trim() !== '' &&
        planForm.annualPrice !== '' && planForm.maxUsers !== '' &&
        planForm.durationMonths !== '';

    const openPayment  = () => { setPaymentForm(EMPTY_PAYMENT); setPaymentError(null); setShowPaymentValidation(false); setPaymentOpen(true); };
    const closePayment = () => { if (paymentSubmitting) return; setPaymentOpen(false); setPaymentError(null); setShowPaymentValidation(false); };
    const updatePayment = (field, value) => setPaymentForm(prev => ({ ...prev, [field]: value }));

    const submitPayment = () => {
        if (paymentSubmitting) return;
        if (!paymentValid) { setShowPaymentValidation(true); return; }
        setShowPaymentValidation(false);
        setPaymentSubmitting(true);
        setPaymentError(null);
        axios.post(ADD_PAYMENT_API_URL, {
            connectionId:    paymentForm.connectionId,
            userId:          paymentForm.userId,
            planId:          paymentForm.planId,
            amount:          Number(paymentForm.amount),
            paymentType:     paymentForm.paymentType,
            subscriptionEnd: paymentForm.subscriptionEnd,
        })
            .then(() => {
                setPaymentOpen(false);
                setPaymentForm(EMPTY_PAYMENT);
                fetchPlans();
                fetchInvoices();
            })
            .catch(err => setPaymentError(err.response?.data?.message || err.message))
            .finally(() => setPaymentSubmitting(false));
    };

    const paymentValid = paymentForm.connectionId !== '' && paymentForm.userId !== '' && paymentForm.planId !== '' &&
        paymentForm.amount !== '' && paymentForm.subscriptionEnd !== '';

    const currentPlan = plans.find(p => p.isCurrent);
    const fmtCount = (n, label) => (n === -1 ? `Unlimited ${label}` : `${n} ${label}`);
    const bannerName     = currentPlan?.name || 'Growth';
    const bannerPriceRaw = currentPlan ? (isAnnual ? currentPlan.annual : currentPlan.monthly) : (isAnnual ? '$249' : '$299');
    const bannerPrice    = bannerPriceRaw === 'Custom' ? 'Custom' : `${bannerPriceRaw}/mo`;
    const bannerInstances = currentPlan ? fmtCount(currentPlan.maxInstances, currentPlan.maxInstances === 1 ? 'instance' : 'instances') : '3 instances';
    const bannerUsers     = currentPlan ? fmtCount(currentPlan.maxUsers, currentPlan.maxUsers === 1 ? 'user' : 'users') : '20 users';
    const nextInvoice = bannerPriceRaw === 'Custom' ? 'Custom' : `${bannerPriceRaw}.00`;

    return (
        <>
            {/* Mobile Menu Overlay */}
            <div className={`mobile-menu-overlay${menuOpen ? ' show' : ''}`} onClick={() => setMenuOpen(false)} />

            {/* Mobile Slide Menu */}
            <div className={`mobile-menu${menuOpen ? ' show' : ''}`}>
                <div className="mobile-menu-header">
                    <a href="#" className="logobock d-flex align-items-center gap-2">
                        <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                        <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                    </a>
                    <div className="mobile-menu-close" onClick={() => setMenuOpen(false)} role="button" aria-label="Close menu">&#10005;</div>
                </div>
                <div className="mobile-menu-items">
                    <div className="mobile-menu-item" onClick={() => navigate('/journeys')}>Journeys</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/data-extensions')}>Data Extensions</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/automations')}>Automations</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/email-studio')}>Email Studio</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/cloud-pages')}>Cloud Pages</div>
                    <div className="mobile-menu-item active">Admin</div>
                </div>
            </div>

            {/* Navbar */}
            <nav className="navbar-custom">
                <div className="adm-navbar-container">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <button className="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                                <span /><span /><span />
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <a href="#" className="logobock d-flex align-items-center gap-2">
                                    <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                                    <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                                </a>
                                <span className="divider-mobile" />
                                <span className="marketing-cloud-text">Admin</span>
                            </div>
                        </div>
                        <button
                            className="environment-badge d-flex align-items-center gap-2"
                            onClick={() => { localStorage.removeItem('isAdminLoggedIn'); localStorage.removeItem('adminAuthToken'); localStorage.removeItem('adminSessionId'); navigate('/admin/login'); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                            <div className="environment-dot" />
                            <span className="environment-text">Logout</span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Page */}
            <div className="adm-page">
                <div className="adm-admin-layout">
                    {/* Sidebar */}
                    <aside className="adm-sidebar">
                        <div className="adm-sidebar-box">
                            <div className="adm-sidebar-header">
                                <p className="adm-sidebar-title">Admin Settings</p>
                            </div>
                            <div className="adm-sidebar-tabs">
                                <button className="adm-sidebar-tab" type="button" onClick={() => navigate('/admin/mc-connections')}>
                                    <span>&#9729;</span> MC Connections
                                </button>
                                <button className="adm-sidebar-tab" type="button" onClick={() => navigate('/admin/user-management')}>
                                    <span>&#128101;</span> User Management
                                </button>
                                <button className="adm-sidebar-tab active" type="button">
                                    <span>&#128179;</span> Billing &amp; Plan
                                </button>
                            </div>
                            <div className="adm-sidebar-back">
                                <button className="adm-btn-back" type="button" onClick={() => navigate('/automations')}>
                                    &larr; Back to AuditHippo
                                </button>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="adm-main">
                        <div className="adm-section-header">
                            <div>
                                <h2 className="adm-section-title">Subscription &amp; Billing</h2>
                                <p className="adm-section-subtitle">Manage your AuditHippo plan, payment method, and invoices</p>
                            </div>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={openPlan}>
                                <span className="me-1">+</span> Add Plan
                            </button>
                        </div>

                        {/* Banner */}
                        <div className="adm-billing-banner">
                            <div>
                                <p className="adm-billing-current-label">Current Plan</p>
                                <h2 className="adm-billing-current-title">{bannerName}</h2>
                                <p className="adm-billing-current-sub">
                                    Renews Apr 1, 2026 &middot; {bannerPrice} &middot; {bannerInstances} &middot; {bannerUsers}
                                </p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div className="adm-billing-next-box">
                                    <p className="adm-billing-next-label">Next invoice</p>
                                    <p className="adm-billing-next-price">{nextInvoice}</p>
                                </div>
                            </div>
                        </div>

                        {/* Toggle */}
                        <div className="adm-toggle-annual-row">
                            <span className="adm-toggle-label">Monthly</span>
                            <button
                                type="button"
                                className={`adm-toggle-annual-btn${isAnnual ? ' is-annual' : ''}`}
                                onClick={() => setIsAnnual(!isAnnual)}
                            >
                                <span className="adm-toggle-annual-dot" />
                            </button>
                            <span className="adm-toggle-label">
                                Annual <span className="adm-billing-save-pill">Save 20%</span>
                            </span>
                        </div>

                        {/* Plan Cards */}
                        <div className="adm-plans-grid">
                            {plans.map(plan => (
                                <div key={plan.id || plan.name} className={`adm-plan-card${plan.isCurrent ? ' adm-plan-current' : ''}`}>
                                    {plan.isCurrent && <span className="adm-plan-current-tag">CURRENT PLAN</span>}
                                    <p className={`adm-plan-name-${plan.nameClass}`}>{plan.name}</p>
                                    <p className="adm-plan-desc">{plan.desc}</p>
                                    <div className="d-flex align-items-baseline gap-2" style={{ marginBottom: 14 }}>
                                        <span className="adm-plan-price-number">
                                            {plan.monthly === 'Custom' ? 'Custom' : (isAnnual ? plan.annual : plan.monthly)}
                                        </span>
                                        {plan.monthly !== 'Custom' && <span className="adm-plan-period">/mo</span>}
                                    </div>
                                    <div className="adm-plan-features">
                                        {plan.features.map(f => (
                                            <div key={f} className="adm-plan-feature">
                                                <span className="adm-plan-feature-check">&#10003;</span>
                                                <span className="adm-plan-feature-text">{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <button type="button" className={`btn-admin ${plan.btnClass} w-100`}>{plan.btnLabel}</button>
                                    <button
                                        type="button"
                                        className="btn-admin btn-admin-danger w-100"
                                        style={{ marginTop: 8 }}
                                        onClick={() => handleRemovePlan(plan)}
                                        disabled={removingPlanId === plan.id}
                                    >
                                        {removingPlanId === plan.id ? 'Deleting…' : '✕ Delete Plan'}
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Add Payment */}
                        <div className="d-flex justify-content-end" style={{ marginBottom: 14 }}>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={openPayment}>
                                <span className="me-1">+</span> Add Payment
                            </button>
                        </div>

                        {/* Invoice History */}
                        <div className="adm-invoice-card">
                            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 14 }}>
                                <p className="adm-card-title">Invoice History</p>
                                <button type="button" className="btn-admin btn-admin-ghost btn-admin-sm">Download All</button>
                            </div>

                            <div className="adm-invoice-rows">
                                <div className="adm-invoice-table-head">
                                    <span className="adm-invoice-th">Connection</span>
                                    <span className="adm-invoice-th">User</span>
                                    <span className="adm-invoice-th">Period</span>
                                    <span className="adm-invoice-th">Date</span>
                                    <span className="adm-invoice-th">Amount</span>
                                    <span className="adm-invoice-th" style={{ textAlign: 'right' }}>Status</span>
                                </div>

                                {invoices.map(inv => {
                                    const matchedConn = connections.find(c => (c.id || c.connectionId) === inv.connectionId);
                                    const connectionName = inv.connectionName
                                        || matchedConn?.name
                                        || matchedConn?.instanceName
                                        || '—';
                                    const matchedUser = users.find(u => (u.id || u.userId) === inv.userId);
                                    const userName = inv.userName
                                        || matchedUser?.name
                                        || matchedUser?.email
                                        || '—';
                                    return (
                                        <div key={inv.id} className="adm-invoice-row">
                                            <span className="adm-invoice-cell">{connectionName}</span>
                                            <span className="adm-invoice-cell">{userName}</span>
                                            <span className="adm-invoice-cell">{inv.period}</span>
                                            <span className="adm-invoice-cell">{inv.date}</span>
                                            <span className="adm-invoice-amount">{inv.amount}</span>
                                            <div className="d-flex align-items-center justify-content-end">
                                                <span className="adm-invoice-paid">{inv.status}</span>
                                            </div>
                                        </div>
                                    );
                                })}

                                {invoices.length === 0 && (
                                    <p style={{ textAlign: 'center', padding: 24, color: '#94A3B8', margin: 0 }}>No invoices yet.</p>
                                )}
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            {/* Offcanvas: Add Plan */}
            {planOpen && (
                <>
                    <div className="adm-offcanvas-overlay" onClick={closePlan} />
                    <div className="adm-offcanvas">
                        <div className="adm-offcanvas-header">
                            <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                                <div>
                                    <h2 className="modal-title-text">Add Plan</h2>
                                    <p className="panel-subtitle">Create a new billing plan</p>
                                </div>
                                <button type="button" className="btn-close-custom" onClick={closePlan} aria-label="Close">&times;</button>
                            </div>
                        </div>

                        <div className="adm-offcanvas-body">
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <label className="adm-input-label">Name</label>
                                    <input className="adm-text-input" type="text" placeholder="e.g. Pro"
                                        style={showPlanValidation && !planForm.name.trim() ? { borderColor: '#EF4444' } : undefined}
                                        value={planForm.name} onChange={e => updatePlan('name', e.target.value)} />
                                    {showPlanValidation && !planForm.name.trim() && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Name is required.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="adm-input-label">Annual Price (cents)</label>
                                    <input className="adm-text-input" type="number" placeholder="16900"
                                        style={showPlanValidation && planForm.annualPrice === '' ? { borderColor: '#EF4444' } : undefined}
                                        value={planForm.annualPrice} onChange={e => updatePlan('annualPrice', e.target.value)} />
                                    {showPlanValidation && planForm.annualPrice === '' && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Annual Price is required.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="adm-input-label">Max Users</label>
                                    <input className="adm-text-input" type="number" placeholder="50"
                                        style={showPlanValidation && planForm.maxUsers === '' ? { borderColor: '#EF4444' } : undefined}
                                        value={planForm.maxUsers} onChange={e => updatePlan('maxUsers', e.target.value)} />
                                    {showPlanValidation && planForm.maxUsers === '' && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Max Users is required.</p>
                                    )}
                                </div>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label className="adm-input-label">Payment Type</label>
                                        <select className="adm-text-input"
                                            value={planForm.paymentType} onChange={e => updatePlan('paymentType', e.target.value)}>
                                            {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="adm-input-label">Duration (months)</label>
                                        <input className="adm-text-input" type="number" placeholder="12"
                                            style={showPlanValidation && planForm.durationMonths === '' ? { borderColor: '#EF4444' } : undefined}
                                            value={planForm.durationMonths} onChange={e => updatePlan('durationMonths', e.target.value)} />
                                        {showPlanValidation && planForm.durationMonths === '' && (
                                            <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Duration is required.</p>
                                        )}
                                    </div>
                                </div>
                                {planError && (
                                    <p style={{ color: '#EF4444', margin: 0 }}>Failed to add plan: {planError}</p>
                                )}
                            </div>
                        </div>

                        <div className="adm-offcanvas-footer">
                            <button type="button" className="btn-admin btn-admin-secondary btn-admin-md" onClick={closePlan} disabled={planSubmitting}>
                                Cancel
                            </button>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={submitPlan} disabled={planSubmitting}>
                                {planSubmitting ? 'Saving…' : 'Add Plan'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Offcanvas: Add Payment */}
            {paymentOpen && (
                <>
                    <div className="adm-offcanvas-overlay" onClick={closePayment} />
                    <div className="adm-offcanvas">
                        <div className="adm-offcanvas-header">
                            <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                                <div>
                                    <h2 className="modal-title-text">Add Payment</h2>
                                    <p className="panel-subtitle">Record a payment against a connection and plan</p>
                                </div>
                                <button type="button" className="btn-close-custom" onClick={closePayment} aria-label="Close">&times;</button>
                            </div>
                        </div>

                        <div className="adm-offcanvas-body">
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <label className="adm-input-label">Connection</label>
                                    <select className="adm-text-input"
                                        style={showPaymentValidation && !paymentForm.connectionId ? { borderColor: '#EF4444' } : undefined}
                                        value={paymentForm.connectionId} onChange={e => setPaymentForm(prev => ({ ...prev, connectionId: e.target.value, userId: '' }))}>
                                        <option value="">Select a connection</option>
                                        {connections.map(c => {
                                            const id   = c.id || c.connectionId;
                                            const name = c.name || c.instanceName || `${c.orgName || 'Instance'}`;
                                            return <option key={id} value={id}>{name}</option>;
                                        })}
                                    </select>
                                    {showPaymentValidation && !paymentForm.connectionId && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Connection is required.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="adm-input-label">User</label>
                                    {(() => {
                                        const selectedConn = connections.find(c => (c.id || c.connectionId) === paymentForm.connectionId);
                                        const connUsers = selectedConn?.users || [];
                                        return (
                                            <>
                                                <select className="adm-text-input"
                                                    style={showPaymentValidation && !paymentForm.userId ? { borderColor: '#EF4444' } : undefined}
                                                    value={paymentForm.userId}
                                                    onChange={e => updatePayment('userId', e.target.value)}
                                                    disabled={!paymentForm.connectionId}>
                                                    <option value="">
                                                        {!paymentForm.connectionId ? 'Select a connection first' : (connUsers.length === 0 ? 'No users on this connection' : 'Select a user')}
                                                    </option>
                                                    {connUsers.map(u => {
                                                        const id   = u.id || u.userId;
                                                        const name = u.name || u.email || id;
                                                        return <option key={id} value={id}>{u.email ? `${name} (${u.email})` : name}</option>;
                                                    })}
                                                </select>
                                            </>
                                        );
                                    })()}
                                    {showPaymentValidation && !paymentForm.userId && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>User is required.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="adm-input-label">Plan</label>
                                    <select className="adm-text-input"
                                        style={showPaymentValidation && !paymentForm.planId ? { borderColor: '#EF4444' } : undefined}
                                        value={paymentForm.planId} onChange={e => updatePayment('planId', e.target.value)}>
                                        <option value="">Select a plan</option>
                                        {plans.map(p => (
                                            <option key={p.id || p.name} value={p.id || ''}>{p.name}</option>
                                        ))}
                                    </select>
                                    {showPaymentValidation && !paymentForm.planId && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Plan is required.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="adm-input-label">Amount (cents)</label>
                                    <input className="adm-text-input" type="number" placeholder="9900"
                                        style={showPaymentValidation && paymentForm.amount === '' ? { borderColor: '#EF4444' } : undefined}
                                        value={paymentForm.amount} onChange={e => updatePayment('amount', e.target.value)} />
                                    {showPaymentValidation && paymentForm.amount === '' && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Amount is required.</p>
                                    )}
                                </div>
                                <div>
                                    <label className="adm-input-label">Payment Type</label>
                                    <select className="adm-text-input"
                                        value={paymentForm.paymentType} onChange={e => updatePayment('paymentType', e.target.value)}>
                                        {PAYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="adm-input-label">Subscription End</label>
                                    <input className="adm-text-input" type="date"
                                        style={showPaymentValidation && !paymentForm.subscriptionEnd ? { borderColor: '#EF4444' } : undefined}
                                        value={paymentForm.subscriptionEnd} onChange={e => updatePayment('subscriptionEnd', e.target.value)} />
                                    {showPaymentValidation && !paymentForm.subscriptionEnd && (
                                        <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Subscription End is required.</p>
                                    )}
                                </div>
                                {paymentError && (
                                    <p style={{ color: '#EF4444', margin: 0 }}>Failed to add payment: {paymentError}</p>
                                )}
                            </div>
                        </div>

                        <div className="adm-offcanvas-footer">
                            <button type="button" className="btn-admin btn-admin-secondary btn-admin-md" onClick={closePayment} disabled={paymentSubmitting}>
                                Cancel
                            </button>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={submitPayment} disabled={paymentSubmitting}>
                                {paymentSubmitting ? 'Saving…' : 'Add Payment'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default BillingPlan;
